import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useRouteLoaderData } from '@remix-run/react';
import { useUser } from '~/utils/hooks/useUser';
import { buildOverrideMap, type OverrideMap } from '~/copy/resolve';
import type { CopyLocale } from '~/copy/registry';
import { CopyEditBridge } from './copy-edit-bridge';

export interface CopyContextValue {
  overrides: OverrideMap;
  editMode: boolean;
  locale: CopyLocale;
}

export const CopyContext = createContext<CopyContextValue>({
  overrides: {},
  editMode: false,
  locale: 'es',
});

export function useCopyContext(): CopyContextValue {
  return useContext(CopyContext);
}

const EDIT_FLAG = 'copyEditMode';
type OverrideRow = { key: string; locale: CopyLocale; value: string };

export function CopyProvider({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useUser();
  // English copy isn't live yet — hardcode 'es' rather than deriving from
  // i18n.language. i18n.language is detected per-request from the visitor's
  // Accept-Language header/cookie (via remix-i18next) and can resolve to
  // 'en' for plenty of real browsers, silently mixing English <Copy> text
  // into an otherwise all-Spanish page (and into the admin preview iframe).
  // Swap this back to detecting i18n.language once English actually ships.
  const locale: CopyLocale = 'es';

  // Overrides come straight from the root loader — SSR-correct, no browser tRPC
  // client, no client fetch. Root re-runs on navigation/revalidation, so edits
  // made in the editor appear on the next page load.
  const rootData = useRouteLoaderData('root') as { copyOverrides?: OverrideRow[] } | undefined;
  const rows = rootData?.copyOverrides ?? [];
  const overrides = useMemo(() => buildOverrideMap(rows), [rows]);

  // Edit mode is client-only: admin session + inside the editor iframe + edit flag.
  // The flag survives internal iframe navigations via sessionStorage.
  const [editMode, setEditMode] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    const inIframe = window.self !== window.top;
    if (!inIframe) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('copyEdit') === '1') {
      sessionStorage.setItem(EDIT_FLAG, '1');
    }
    if (sessionStorage.getItem(EDIT_FLAG) === '1') setEditMode(true);
  }, [isAdmin]);

  const value = useMemo<CopyContextValue>(() => ({ overrides, editMode, locale }), [overrides, editMode, locale]);
  return (
    <CopyContext.Provider value={value}>
      {children}
      {editMode ? <CopyEditBridge /> : null}
    </CopyContext.Provider>
  );
}
